import React from 'react';
import styled from 'styled-components';
import { StaticQuery, graphql } from 'gatsby';

import { Section, Container, BlurredBg } from '@components/global';
import AnimatedStroke from '@common/AnimatedStroke';
import Polaroid from '@common/Polaroid';
import ScrollMotion from '@common/ScrollMotion';

import { ReactComponent as WaveIcon } from '@static/icons/wave.svg';
import { ReactComponent as PlantIcon } from '@static/icons/plant.svg';
import { ReactComponent as EcoIcon } from '@static/icons/eco.svg';

import { ReactComponent as Squiggle } from '@static/squiggles/squiggle-4.svg';

const Discover = () => (
  <StaticQuery
    query={graphql`
      query {
        image_one: file(
          sourceInstanceName: { eq: "polaroid" }
          name: { eq: "polaroid-3" }
        ) {
          childImageSharp {
            fluid(maxWidth: 720, quality: 100) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }

        image_two: file(
          sourceInstanceName: { eq: "polaroid" }
          name: { eq: "polaroid-4" }
        ) {
          childImageSharp {
            fluid(maxWidth: 560, quality: 100) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
      }
    `}
    render={data => (
      <Section>
        <Container>
          <StyledBlurredBg>
            <Grid>
              <div>
                <StyledH2>
                  Discover Advanced
                  <br />
                  Profession Haircare.
                  <br />
                  Discover <Cursive>iluvia</Cursive>
                </StyledH2>
              </div>
              <div>
                <h3>Pioneering research in</h3>
                <List>
                  <li>
                    <span>
                      <WaveIcon />
                    </span>
                    <span>
                      Mitigating environment based hair care concerns.
                    </span>
                  </li>
                  <li>
                    <span>
                      <PlantIcon />
                    </span>
                    <span>
                      Utilizing natural origin biocompatible ingredients.
                    </span>
                  </li>
                  <li>
                    <span>
                      <EcoIcon />
                    </span>
                    <span>
                      Eco-conscious design to reduce environmental impact.
                    </span>
                  </li>
                </List>
              </div>
            </Grid>
          </StyledBlurredBg>
          <Grid alternate>
            <div>
              <ScrollMotion
                translate={{ start: 0, end: -64 }}
                rotate={{ start: -1, end: -4 }}
              >
                <StyledPolaroidOne
                  fluid={data.image_one.childImageSharp.fluid}
                />
              </ScrollMotion>
              <StyledPolaroidTwo fluid={data.image_two.childImageSharp.fluid} />
              <BlurredBg style={{ position: 'relative' }}>
                <CursiveH2>
                  <Cursive>
                    Discover salon trusted solutions that deliver exacting
                    results for the most demanding hair professionals.
                  </Cursive>
                </CursiveH2>
              </BlurredBg>
            </div>
            <div style={{ position: 'relative' }}>
              <AnimatedStroke svg={<StyledSquiggle />} />
              <ScrollMotion
                translate={{
                  start: 0,
                  end: -64,
                }}
                rotate={{
                  start: 1,
                  end: 3,
                }}
              >
                <Polaroid
                  style={{
                    marginLeft: '3rem',
                    minWidth: 500,
                  }}
                  fluid={data.image_two.childImageSharp.fluid}
                />
              </ScrollMotion>
            </div>
          </Grid>
        </Container>
      </Section>
    )}
  />
);

const Grid = styled.div`
  display: grid;
  grid-template-columns: ${props => (props.alternate ? '3fr 2fr' : '1fr 1fr')};
  grid-gap: 2.5rem;
  justify-items: center;
  align-items: center;

  @media (max-width: ${props => props.theme.screen.lg}) {
    justify-items: left;
  }

  @media (max-width: ${props => props.theme.screen.md}) {
    grid-template-columns: 1fr;
    text-align: center;
    justify-items: center;

    > :last-child {
      display: none;
    }
  }
`;

const Cursive = styled.span`
  font-family: ${props => props.theme.font.cursive};
  color: ${props => props.theme.color.blue[1].light};
  display: inline-block;
  line-height: 160%;

  @media (max-width: ${props => props.theme.screen.md}) {
    transform: rotate(1.7deg);
  }
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  margin: 0.5rem 0;

  li {
    display: grid;
    grid-template-columns: 24px 1fr;
    grid-gap: 1rem;
    padding: 0.5rem 0;
    ${props => props.theme.font_size.large};
    color: ${props => props.theme.color.blue[1].dark};

    span:first-child {
      justify-self: center;
    }

    &:not(:last-child) {
      border-bottom: 1px solid ${props => props.theme.color.blue[2].light};
    }
  }
`;

const StyledSquiggle = styled(Squiggle)`
  position: absolute;
  z-index: -1;
  top: -50%;
  left: 32%;
`;

const StyledBlurredBg = styled(BlurredBg)`
  padding: 0 1rem 14rem;
  margin-top: 4rem;

  &::before {
    filter: blur(100px);
  }

  @media (max-width: ${props => props.theme.screen.lg}) {
    padding: 4rem 0 12rem;
  }

  @media (max-width: ${props => props.theme.screen.md}) {
    margin-top: -1rem;
    padding-top: 0;
    padding-bottom: 5rem;
  }
`;

const StyledPolaroidOne = styled(Polaroid)`
  z-index: 1;

  @media (max-width: ${props => props.theme.screen.md}) {
    width: 90%;
    transform: rotate(5.5deg) translateX(-3%);
    display: block;
  }
`;

const StyledPolaroidTwo = styled(Polaroid)`
  display: none;
  width: 90%;
  z-index: 2;
  transform: rotate(-9deg) translateX(32%);

  @media (max-width: ${props => props.theme.screen.md}) {
    display: block;
  }
`;

const StyledH2 = styled.h2`
  ${props => props.theme.font_size.xxxlarge};

  @media (max-width: ${props => props.theme.screen.md}) {
    font-size: 1.75rem;
  }
`;

const CursiveH2 = styled.h2`
  margin-top: 5rem;
  width: 100%;
  font-size: 2rem;

  @media (max-width: ${props => props.theme.screen.md}) {
    font-size: 1.5rem;
  }
`;

export default Discover;
